/**
 * client/src/pages/Share.tsx — `/b/:bookId/share`. Who can open this book, and every key you cut.
 *
 * ── Why this is a screen and not a popover ──────────────────────────────────────────────────────
 *
 * A share link is a **capability that outlives the moment you made it**. It sits in somebody's
 * messages for a year, it gets forwarded, and the only way to take it back is to come back here. A
 * popover is a fine place to *make* one and a terrible place to keep a list you must be able to audit
 * — "which of these did I send Mum, and has anybody else opened it?" is the question this screen
 * exists to answer, and it is why `useCount` and `lastUsedAt` are on the row.
 *
 * ── The one thing this screen must never do ─────────────────────────────────────────────────────
 *
 * Show a link and be wrong about what it grants. Two of the three per-link switches — download and
 * rooms — name features that **are not built** (P6 and P5). They are stored, they are honest about
 * being inert, and they say so on the control rather than in a comment: AGENTS.md's `<Soon>` rule has
 * an expensive second half, which is that a `disabled` prop and a sentence of prose are not
 * greppable, and books.karti.ai shipped a button badged *"Needs voice capture — Phase 6"* that was
 * bolted shut against a feature which had landed in Phase 6.
 *
 * ── Mobile-first at 390 px ──────────────────────────────────────────────────────────────────────
 *
 * Every link is a card that stacks: label, then the URL in a full-width readonly input with its own
 * Copy button, then the facts, then Revoke. Nothing is a table — a table of URLs at 390 px is a
 * horizontal scrollbar with a secret in it.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { canEdit } from "../../../shared/access.ts";
import type { BookMeta } from "../../../shared/doc.ts";
import { AppFrame, Blank, IconAlert, IconBack, IconCheck, timeAgo } from "../App.tsx";
import { ShareNotice, VisibilityMenu } from "../components/VisibilityMenu.tsx";
import { ApiError, abApi, refusalCopy } from "../lib/api.ts";
import type { BookDetail, ShareLink } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { useCachedQuery } from "../lib/idb.ts";

/**
 * The expiry choices, in days.
 *
 * Days rather than a date picker, and a short list rather than a free number, because the question a
 * person actually has is *"how long do I want to leave this open?"* — and because the wire carries
 * absolute milliseconds, which is exactly the field a hand-typed date gets wrong by a timezone.
 */
const EXPIRIES: { label: string; days: number | null }[] = [
  { label: "No expiry", days: null },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "A year", days: 365 },
];

export function Share() {
  const { bookId = "" } = useParams<{ bookId: string }>();
  const { actor } = useAuth();
  const navigate = useNavigate();

  const detail = useCachedQuery<BookDetail>(`book:${bookId}`, () => abApi.book(bookId));
  const [book, setBook] = useState<BookMeta | null>(null);
  const fetched = detail.data?.book ?? null;
  useEffect(() => {
    if (fetched) setBook(fetched);
  }, [fetched]);

  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [linksError, setLinksError] = useState<string | null>(null);
  const [limit, setLimit] = useState(0);

  const loadLinks = useCallback(async () => {
    try {
      const res = await abApi.shares(bookId);
      setLinks(res.links);
      setLimit(res.limit);
      setLinksError(null);
    } catch (err) {
      // A 404 here is LAW 17 doing its job on somebody who is not the owner — it is not a missing
      // feature, and the message must not suggest retrying.
      setLinksError(err instanceof ApiError ? err.message : "Couldn't read the links.");
    }
  }, [bookId]);

  useEffect(() => {
    void loadLinks();
  }, [loadLinks]);

  if (detail.loading && !detail.data) {
    return (
      <AppFrame>
        <Blank />
      </AppFrame>
    );
  }

  if (!book) {
    const missing = detail.error instanceof ApiError && detail.error.notFound;
    return (
      <AppFrame>
        <div className="ab-empty">
          <h2>{missing ? "No book here." : "Couldn't open that book."}</h2>
          <p>{missing ? "It was deleted, or it isn't yours." : (detail.error?.message ?? "Something went wrong.")}</p>
          <Link to="/" className="ab-btn ab-btn-primary">
            Back to the shelf
          </Link>
        </div>
      </AppFrame>
    );
  }

  // The server refuses anyway (404, never 403). This is the courtesy half of the same rule — and it
  // is computed by `canEdit` from shared/access.ts, never from a flag the server sent (LAW 13).
  if (!canEdit(book, actor)) {
    return (
      <AppFrame>
        <div className="ab-empty">
          <h2>Not your book.</h2>
          <p>Only the owner can hand out links to a book.</p>
          <Link to={`/b/${book.id}/read`} className="ab-btn ab-btn-primary">
            Read it instead
          </Link>
        </div>
      </AppFrame>
    );
  }

  const liveCount = links?.filter((l) => l.live).length ?? 0;

  return (
    <AppFrame>
      <button type="button" className="ab-back" onClick={() => navigate(`/b/${book.id}`)}>
        <IconBack />
        <span>{book.title}</span>
      </button>

      <ShareNotice />

      <h1 className="ab-h1">Sharing</h1>
      <p className="ab-lede">
        Send someone a link and they read and listen with no account at all — the voice, the
        highlight, the page turning itself. Take the book back and they lose it on the next page:
        every link is checked against the table on every request, so revoking one means <em>now</em>,
        not when it expires.
      </p>

      <section className="ab-panel">
        <h2 className="ab-h2">Visibility</h2>
        <VisibilityMenu book={book} actor={actor} onChanged={setBook} />
      </section>

      {book.takenDownAt !== null ? (
        <section className="ab-panel ab-panel-bad">
          <h2 className="ab-h2">
            <IconAlert /> Taken down
          </h2>
          <p>This book can&rsquo;t be shared while that stands, and every link on it was revoked.</p>
        </section>
      ) : (
        <NewLink
          bookId={book.id}
          full={links !== null && limit > 0 && liveCount >= limit}
          limit={limit}
          onCreated={(link) => setLinks((prev) => [link, ...(prev ?? [])])}
        />
      )}

      <section className="ab-panel">
        <h2 className="ab-h2">Links</h2>
        {linksError && <p className="ab-hint ab-hint-bad">{linksError}</p>}
        {links === null && !linksError && <p className="ab-hint">Reading the links…</p>}
        {links !== null && links.length === 0 && (
          <p className="ab-hint">No links yet. The one above is the first.</p>
        )}
        {links?.map((link) => (
          <LinkRow key={link.token} link={link} onRevoked={() => void loadLinks()} />
        ))}
      </section>
    </AppFrame>
  );
}

// ─── making one ──────────────────────────────────────────────────────────────

function NewLink({
  bookId,
  full,
  limit,
  onCreated,
}: {
  bookId: string;
  full: boolean;
  limit: number;
  onCreated: (link: ShareLink) => void;
}) {
  const [label, setLabel] = useState("");
  const [days, setDays] = useState<number | null>(null);
  const [allowRegenerate, setAllowRegenerate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ title: string; fix?: string } | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const res = await abApi.createShare(bookId, {
        label: label.trim() || undefined,
        // One unit on the wire. `days` is what a person chose; milliseconds is what `share_links`
        // stores, and converting here means the server never has to guess which one it was handed.
        expiresAt: days === null ? null : Date.now() + days * 24 * 60 * 60 * 1000,
        allowRegenerate,
      });
      onCreated(res.link);
      setLabel("");
      setAllowRegenerate(false);
    } catch (err) {
      const reason = err instanceof ApiError ? err.reason : "";
      setError(refusalCopy(reason) ?? { title: err instanceof ApiError ? err.message : "Couldn't make a link." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">A new link</h2>

      <fieldset className="ab-fields" disabled={busy || full}>
        <div className="ab-field">
          <label htmlFor="ab-share-label">Who is it for?</label>
          <input
            id="ab-share-label"
            type="text"
            value={label}
            maxLength={80}
            placeholder="Mum"
            onChange={(e) => setLabel(e.target.value)}
          />
          <p className="ab-hint">
            Only you ever see this. It is how you tell the link you sent one person from the one you
            pasted into a group chat.
          </p>
        </div>

        <div className="ab-field">
          <label htmlFor="ab-share-expiry">How long</label>
          <div className="ab-segment ab-segment-full" id="ab-share-expiry" role="group" aria-label="How long">
            {EXPIRIES.map((e) => (
              <button
                key={e.label}
                type="button"
                className={e.days === days ? "ab-segment-item is-on" : "ab-segment-item"}
                aria-pressed={e.days === days}
                onClick={() => setDays(e.days)}
              >
                {e.label}
              </button>
            ))}
          </div>
        </div>

        <label className="ab-check">
          <input
            type="checkbox"
            checked={allowRegenerate}
            onChange={(e) => setAllowRegenerate(e.target.checked)}
          />
          <span>
            <strong>Let them fix a sentence.</strong> Anyone with a link can already read, listen,
            and report a sentence that came out wrong — that report reaches you either way. This one
            switch also lets them <em>regenerate</em> it, which changes the audio every other
            listener hears and spends GPU. Off unless you mean it.
          </span>
        </label>
      </fieldset>

      {/* ⚠️ Not a `disabled` checkbox with a phase number on it — see the file header. The two
          switches this product does not have yet are named, with the phase, in one honest line, and
          the line will read correctly right up until the phase lands. */}
      <p className="ab-phase-note">
        <span className="ab-chip ab-chip-soon">P5 · P6</span>
        <span>
          Every link may also carry <em>download</em> and <em>join a reading room</em>. Neither of
          those features exists yet, so there is nothing to switch on; the links you make now will
          carry the default the day they do.
        </span>
      </p>

      {full && (
        <p className="ab-hint ab-hint-bad">
          This book already has {limit} live links, which is the limit. Revoke one to make another.
        </p>
      )}

      {error && (
        <p className="ab-refusal">
          <span className="ab-refusal-title">{error.title}</span>
          {error.fix && <span> {error.fix}</span>}
        </p>
      )}

      <div className="ab-actions">
        <button type="button" className="ab-btn ab-btn-primary" disabled={busy || full} onClick={() => void create()}>
          {busy ? "Making it…" : "Make a link"}
        </button>
      </div>
    </section>
  );
}

// ─── one link ────────────────────────────────────────────────────────────────

function LinkRow({ link, onRevoked }: { link: ShareLink; onRevoked: () => void }) {
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function copy(input: HTMLInputElement | null) {
    /**
     * ⚠️ `navigator.clipboard` is **undefined on an insecure origin**, and this app has one: dev runs
     * on `http://localhost:5191` and an admin opening the site over the tailnet gets plain http too.
     * An unguarded `writeText` throws there, the Copy button does nothing, and the failure looks like
     * the link being broken rather than the clipboard being unavailable. So: try it, and on any
     * failure select the text and say what to press.
     */
    try {
      if (!navigator.clipboard) throw new Error("no clipboard");
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setManual(false);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      input?.focus();
      input?.select();
      setManual(true);
    }
  }

  async function revoke() {
    if (!window.confirm("Revoke this link? Anyone using it loses the book on their next page.")) return;
    setBusy(true);
    setError(null);
    try {
      await abApi.revokeShare(link.token);
      onRevoked();
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError ? err.message : "Couldn't revoke it.");
    }
  }

  let inputEl: HTMLInputElement | null = null;

  return (
    <article className="ab-decision">
      <div className="ab-decision-main">
        <p className="ab-decision-title">{link.label ?? "Untitled link"}</p>

        <div className="ab-card-meta">
          {link.live ? (
            <span className="ab-chip ab-chip-voice">live</span>
          ) : link.revokedAt !== null ? (
            <span className="ab-chip ab-chip-bad">revoked</span>
          ) : (
            <span className="ab-chip ab-chip-warn">expired</span>
          )}
          {link.allowRegenerate && <span className="ab-chip ab-chip-warn">can fix sentences</span>}
          <span className="ab-chip ab-chip-quiet">
            {link.useCount === 0
              ? "never opened"
              : `${link.useCount} open${link.useCount === 1 ? "" : "s"}${link.lastUsedAt ? `, last ${timeAgo(link.lastUsedAt)}` : ""}`}
          </span>
          <span className="ab-chip ab-chip-quiet">
            {link.expiresAt === null
              ? "no expiry"
              : link.expired
                ? `expired ${timeAgo(link.expiresAt)}`
                : `expires ${new Date(link.expiresAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`}
          </span>
        </div>

        {link.live && (
          <div className="ab-field">
            {/* Readonly rather than disabled: a disabled input cannot be selected, which is exactly
                the fallback the Copy button needs on an insecure origin. */}
            <input
              ref={(el) => {
                inputEl = el;
              }}
              type="text"
              readOnly
              value={link.url}
              aria-label={`Share link${link.label ? ` for ${link.label}` : ""}`}
              onFocus={(e) => e.currentTarget.select()}
            />
            <div className="ab-actions">
              <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" onClick={() => void copy(inputEl)}>
                {copied ? (
                  <>
                    <IconCheck />
                    <span>Copied</span>
                  </>
                ) : (
                  <span>Copy</span>
                )}
              </button>
            </div>
            {manual && <p className="ab-hint">Selected — press ⌘C (or Ctrl+C) to copy.</p>}
          </div>
        )}

        {link.live && (
          <div className="ab-actions">
            <button type="button" className="ab-btn ab-btn-danger ab-btn-sm" disabled={busy} onClick={() => void revoke()}>
              {busy ? "Revoking…" : "Revoke"}
            </button>
          </div>
        )}

        {error && <p className="ab-hint ab-hint-bad">{error}</p>}

        <p className="ab-hint ab-hint-quiet">made {timeAgo(link.createdAt)}</p>
      </div>
    </article>
  );
}
